import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useState} from 'react';

import {StyledTextArea} from '../../../StyledTextarea';
import {PlaygroundMessageRole} from '../types';

type PlaygroundChatInputProps = {
  chatText: string;
  setChatText: (text: string) => void;
  isLoading?: boolean;
  onSend?: (role: PlaygroundMessageRole, chatText: string) => void;
  onAdd: (role: PlaygroundMessageRole, chatText: string) => void;
  hasConfiguredProviders?: boolean;
  defaultMessageRole?: PlaygroundMessageRole;
  disableSend?: string;
  cancelGeneration?: () => void;
};

export const PlaygroundChatInput: React.FC<PlaygroundChatInputProps> = ({
  chatText,
  setChatText,
  isLoading = false,
  onSend,
  onAdd,
  hasConfiguredProviders = true,
  defaultMessageRole = 'user',
  disableSend,
  cancelGeneration,
}) => {
  const disableChatInput =
    isLoading ||
    chatText.trim() === '' ||
    !hasConfiguredProviders ||
    !!disableSend;

  const [addMessageRole, setAddMessageRole] =
    useState<PlaygroundMessageRole>(defaultMessageRole);
  const [shouldReset, setShouldReset] = useState(false);
  const [isSendButtonHovered, setIsSendButtonHovered] = useState(false);

  const handleReset = () => {
    setShouldReset(true);
    setTimeout(() => setShouldReset(false), 0);
  };

  const handleSend = (role: PlaygroundMessageRole) => {
    onSend?.(role, chatText);
    handleReset();
  };

  const handleAdd = (role: PlaygroundMessageRole, text: string) => {
    onAdd(role, text);
    handleReset();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !disableChatInput) {
      event.preventDefault();
      handleSend(addMessageRole);
    }
  };

  return (
    // WARN: z-index position of navbar overflow menu is `2`, check first if changing
    <div className="z-1 flex min-h-[160px] w-full flex-shrink-0 bg-white">
      <div className="mx-auto w-full max-w-[800px]">
        {onSend && (
          <div className="mb-8 flex items-center justify-end gap-4 text-xs text-moon-500">
            Press Enter to send
            <Tooltip
              trigger={<Icon className="w-14 h-14" name="help-alt" />}
              content="Hold Shift + Enter to create a newline"
            />
          </div>
        )}
        <StyledTextArea
          onChange={e => setChatText(e.target.value)}
          value={chatText}
          onKeyDown={handleKeyDown}
          placeholder={
            hasConfiguredProviders
              ? 'Type your message here...'
              : 'Configure a provider to start chatting...'
          }
          autoGrow
          maxHeight={160}
          reset={shouldReset}
          disabled={!hasConfiguredProviders}
        />
        <div className="flex justify-between">
          <div className="flex gap-8">
            {/* TODO: Add image upload */}
            {/* <Button variant="secondary" size="small" startIcon="photo" /> */}
          </div>
          <div className="flex gap-8">
            <div className="flex items-center text-xs text-moon-500">
              Add as
              <Button
                className="ml-4 rounded-r-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'system'}
                onClick={() => setAddMessageRole('system')}>
                System
              </Button>
              <Button
                className="rounded-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'assistant'}
                onClick={() => setAddMessageRole('assistant')}>
                Assistant
              </Button>
              <Button
                className="rounded-l-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'user'}
                onClick={() => setAddMessageRole('user')}>
                User
              </Button>
            </div>
            <Button
              variant="secondary"
              size="medium"
              startIcon="add-new"
              disabled={
                isLoading || chatText.trim() === '' || !hasConfiguredProviders
              }
              onClick={() => handleAdd(addMessageRole, chatText)}>
              Add
            </Button>
            {onSend && (
              <>
                <div className="h-full w-px bg-moon-250" />
                <Button
                  size="medium"
                  onClick={() => {
                    if (isLoading && cancelGeneration) {
                      cancelGeneration();
                    } else {
                      handleSend(addMessageRole);
                    }
                  }}
                  onMouseEnter={() => setIsSendButtonHovered(true)}
                  onMouseLeave={() => setIsSendButtonHovered(false)}
                  disabled={disableChatInput && !isLoading}
                  className={isLoading ? 'opacity-35' : ''}
                  startIcon={
                    isLoading && isSendButtonHovered ? 'close' : undefined
                  }
                  tooltip={disableSend || undefined}>
                  {isLoading
                    ? isSendButtonHovered
                      ? 'Cancel'
                      : 'Sending...'
                    : 'Send'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
